import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Button, IconButton, ListItemText, Menu, MenuItem, MenuList, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { trackDeployClicked } from '../../../analytics/bpsEvents';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { selectIsDrivebaseDistributingFirmware, selectIsDrivebaseRuntimeBusy, selectIsProgramActive } from '../../../redux/RobotSlice';
import { RootState } from '../../../redux/Store';
import { ApplicationActions, RobotActions } from '../../../utils/constants';
import { areLinterDiagnosticsCurrentForLoadedProject, hasBlockingLinterErrors } from '../../../utils/linterDiagnostics';
import type { LinterIgnoreIntent, PendingLinterIgnore } from '../../../utils/linterIgnoreIntent';
import { selectValidSessionSaveSlotNumber } from '../../../redux/ProjectSlice';
import { ResetOrientationIcon } from '../../Icons/Editor Panels';
import { useResetOrientationAcknowledgement } from '../../../hooks/ui/useResetOrientationAcknowledgement';
import SlotButton from './SlotButton';

// Give the robot/runtime state a brief window to settle after Stop before another Run can be queued.
const STOP_TO_RUN_COOLDOWN_MS = 500;

type LinterBypassSnapshot = {
  projectId: string | null;
  programCode: string;
};

const DeployButton = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  // Switch to icon-only below this breakpoint (accounts for drawer width at runtime)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1000));

  // Deploy/Run state
  const [runMenuAnchor, setRunMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [runState, setRunState] = React.useState('download_run');
  const [isDeploying, setIsDeploying] = useState(false);
  const [isStopToRunCooldownActive, setIsStopToRunCooldownActive] = useState(false);
  const [openSlotMenuSignal, setOpenSlotMenuSignal] = useState(0);
  const [pendingSlotProgramAction, setPendingSlotProgramAction] = useState(false);
  const { programState, loadedProject, diagnostics, diagnosticsProjectId, diagnosticsProgramCode } = useSelector((state: RootState) => state.project);
  const sessionSaveSlotNumber = useAppSelector(selectValidSessionSaveSlotNumber);
  const prevProgramState = React.useRef(programState);
  const stopToRunCooldownTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const linterErrorsBypassedRef = React.useRef<LinterBypassSnapshot | null>(null);
  const { robotState, programActionsReady } = useSelector((state: RootState) => state.robot);
  const isProgramActive = useAppSelector(selectIsProgramActive);
  const isRuntimeBusy = useAppSelector(selectIsDrivebaseRuntimeBusy);
  const isDistributingFirmware = useAppSelector(selectIsDrivebaseDistributingFirmware);
  const slotStorageReady = useAppSelector((state) => state.robotData.slotStorageReady);
  const slotStorageFresh = useAppSelector((state) => state.robotData.slotStorageFresh);
  const { userAgentInfo, enableToolBar, autoSwapToDashboard, pendingLinterIgnore } = useSelector((state: RootState) => state.application) as {
    userAgentInfo: { isBrowserSupported: boolean } | null;
    enableToolBar: boolean;
    autoSwapToDashboard: boolean;
    pendingLinterIgnore: PendingLinterIgnore | null;
  };
  const openDrawer = useAppSelector((state) => state.project.openDrawer);
  const { BLUETOOTH_WARNING_DIALOG, LINTER_ERRORS_DIALOG } = ApplicationActions.Dialogs;
  const isConnected = robotState === RobotActions.STATE.CONNECTED;
  const serviceCanAcceptProgramAction =
    typeof window.robotService?.canAcceptProgramActionFromUi === 'function'
      ? window.robotService.canAcceptProgramActionFromUi()
      : programActionsReady;
  const serviceCanPauseBackgroundFirmwareForProgramAction =
    typeof window.robotService?.canPauseBackgroundFirmwareForProgramAction === 'function'
      ? window.robotService.canPauseBackgroundFirmwareForProgramAction()
      : false;
  const isDeployQueued = programState === RobotActions.PROGRAM.DOWNLOAD || programState === RobotActions.PROGRAM.DOWNLOAD_RUN;
  const isDeployingProgram = isDeploying || isDeployQueued;
  const deployActionsReady = programActionsReady && serviceCanAcceptProgramAction;
  const waitingForProgramActions = isConnected && !deployActionsReady && !isProgramActive && !isDeployingProgram;
  const slotStorageQuerying = waitingForProgramActions && !slotStorageReady;
  const programCode = loadedProject?.program.code ?? '';
  const linterDiagnosticsReady = areLinterDiagnosticsCurrentForLoadedProject(
    loadedProject?.id,
    diagnosticsProjectId,
    programCode,
    diagnosticsProgramCode
  );
  const waitingForLinterDiagnostics = isConnected && !isProgramActive && Boolean(programCode) && !linterDiagnosticsReady;
  const { resetOrientation } = useResetOrientationAcknowledgement();

  const clearLinterBypass = () => {
    linterErrorsBypassedRef.current = null;
  };

  const hasCurrentLinterBypass = () =>
    linterErrorsBypassedRef.current?.projectId === (loadedProject?.id ?? null) && linterErrorsBypassedRef.current?.programCode === programCode;

  const getRunButtonText = () => {
    if (isProgramActive) return t('downloadCodeButton.stop', 'Stop');
    if (isDeployingProgram) return t('downloadCodeButton.deploying', 'Deploying...');
    if (waitingForLinterDiagnostics) return t('downloadCodeButton.checking-code', 'Checking Code...');
    if (slotStorageQuerying) return t('downloadCodeButton.checking-slots', 'Checking Slots...');
    if (waitingForProgramActions) return t('downloadCodeButton.preparing-robot', 'Preparing Robot...');
    if (runState === 'download_run') return t('downloadCodeButton.download-and-run', 'Download and Run');
    if (runState === 'run') return t('downloadCodeButton.run', 'Run');
    return t('downloadCodeButton.download', 'Download');
  };

  const navigateToDashboard = React.useCallback(() => {
    if (autoSwapToDashboard) {
      dispatch(projectSlice.actions.setDashboardOpenDrawer(false));
      const dashboardPath = loadedProject?.id ? `/dashboard/${loadedProject.id}` : '/dashboard';
      navigate(dashboardPath);
    }
  }, [autoSwapToDashboard, dispatch, loadedProject?.id, navigate]);

  useEffect(
    () => () => {
      if (stopToRunCooldownTimeoutRef.current !== null) {
        clearTimeout(stopToRunCooldownTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const prevState = prevProgramState.current;
    prevProgramState.current = programState;
    const isNewTransition = prevState !== programState;

    switch (programState) {
      case RobotActions.PROGRAM.STARTING:
        if (isNewTransition) {
          dispatch(projectSlice.actions.onAddConsoleDisplay(t('console-log.program-run', 'Program starting')));
        }
        setIsDeploying(false);
        if (isNewTransition) {
          navigateToDashboard();
        }
        break;
      case RobotActions.PROGRAM.STARTED:
        if (isNewTransition) {
          dispatch(projectSlice.actions.onAddConsoleDisplay(t('console-log.program-run', 'Program started')));
        }
        setIsDeploying(false);
        if (isNewTransition) {
          navigateToDashboard();
        }
        break;
      case RobotActions.PROGRAM.STOPPED:
      case RobotActions.PROGRAM.STOP:
        setIsDeploying(false);
        break;
      case RobotActions.PROGRAM.SEND_STARTING:
        if (!isDeploying) {
          setIsDeploying(true);
          dispatch(projectSlice.actions.onAddConsoleDisplay(t('console-log.program-send-starting', 'Program sending starting')));
        }
        break;
      case RobotActions.PROGRAM.SEND_SUCCEEDED:
        if (isDeploying) {
          setIsDeploying(false);
          dispatch(projectSlice.actions.onAddConsoleDisplay(t('console-log.program-send-succeeded', 'Program sending succeeded')));
        }
        break;
      case RobotActions.PROGRAM.SEND_FAILED:
        setIsDeploying(false);
        if (isNewTransition) {
          dispatch(projectSlice.actions.onAddConsoleDisplay(t('console-log.program-send-failed', 'Program sending failed')));
        }
        break;
      case RobotActions.PROGRAM.ERROR:
        setIsDeploying(false);
        break;
    }
  }, [programState, isDeploying, dispatch, t, navigateToDashboard]);

  // Deploy/Run handlers
  const startProgram = (targetSlotNumber = sessionSaveSlotNumber) => {
    const programStateAction =
      runState === 'run'
        ? { programState: RobotActions.PROGRAM.RUN, runMode: 'run' as const, targetSlotNumber }
        : runState === 'download'
        ? { programState: RobotActions.PROGRAM.DOWNLOAD, runMode: 'download' as const, targetSlotNumber }
        : { programState: RobotActions.PROGRAM.DOWNLOAD_RUN, runMode: 'download_run' as const, targetSlotNumber };

    dispatch(projectSlice.actions.onUpdateProgramState(programStateAction));
  };

  const stopProgram = () => {
    setIsStopToRunCooldownActive(true);
    if (stopToRunCooldownTimeoutRef.current !== null) {
      clearTimeout(stopToRunCooldownTimeoutRef.current);
    }
    stopToRunCooldownTimeoutRef.current = setTimeout(() => {
      setIsStopToRunCooldownActive(false);
      stopToRunCooldownTimeoutRef.current = null;
    }, STOP_TO_RUN_COOLDOWN_MS);

    dispatch(
      projectSlice.actions.onUpdateProgramState({
        programState: RobotActions.PROGRAM.STOP,
      })
    );
    dispatch(projectSlice.actions.onAddConsoleDisplay(t('console-log.program-stop', 'Program stopped')));
  };

  const toggleProgramState = (targetSlotNumber = sessionSaveSlotNumber) => {
    if (isProgramActive) {
      stopProgram();
    } else {
      startProgram(targetSlotNumber);
    }
  };

  const continueProgramAction = (targetSlotNumber = sessionSaveSlotNumber) => {
    if (!isProgramActive && !isDeployingProgram) {
      const deployType: 'Deploy' | 'Deploy & Run' | 'Run' = runState === 'download_run' ? 'Deploy & Run' : runState === 'download' ? 'Deploy' : 'Run';
      trackDeployClicked(deployType);
    }
    toggleProgramState(targetSlotNumber);
  };

  const openLinterErrorsDialog = (intent: LinterIgnoreIntent) => {
    dispatch(applicationSlice.actions.setPendingLinterIgnore(intent));
    dispatch(
      applicationSlice.actions.onOpenDialog({
        dialog: LINTER_ERRORS_DIALOG.dialog,
        options: {
          ...LINTER_ERRORS_DIALOG.options,
          diagnostics,
          linterIgnoreAction: runState === 'run' ? 'run' : 'deploy',
        },
      })
    );
  };

  const shouldSkipLinterCheck = (skipLinterCheck = false) => skipLinterCheck || hasCurrentLinterBypass();

  const attemptProgramAction = (targetSlotNumber = sessionSaveSlotNumber, options: { skipLinterCheck?: boolean } = {}) => {
    if (
      !isProgramActive &&
      !isDeployingProgram &&
      !shouldSkipLinterCheck(options.skipLinterCheck) &&
      linterDiagnosticsReady &&
      hasBlockingLinterErrors(diagnostics)
    ) {
      openLinterErrorsDialog({
        flow: 'attemptProgramAction',
        projectId: loadedProject?.id ?? null,
        programCode,
        targetSlotNumber: targetSlotNumber ?? sessionSaveSlotNumber ?? 0,
      });
      return;
    }

    if (dispatchStaleRunValidationFailureIfNeeded()) {
      clearLinterBypass();
      return;
    }

    clearLinterBypass();
    continueProgramAction(targetSlotNumber);
  };

  const resumeConnectedDeployFlow = (options: { skipLinterCheck?: boolean } = {}) => {
    if (
      !isProgramActive &&
      !isDeployingProgram &&
      !shouldSkipLinterCheck(options.skipLinterCheck) &&
      linterDiagnosticsReady &&
      hasBlockingLinterErrors(diagnostics)
    ) {
      openLinterErrorsDialog({ flow: 'resumeConnectedDeploy', projectId: loadedProject?.id ?? null, programCode });
      return;
    }

    if (sessionSaveSlotNumber === null || shouldConfirmUnverifiedDeployTarget) {
      setPendingSlotProgramAction(true);
      setOpenSlotMenuSignal((signal) => signal + 1);
      return;
    }

    attemptProgramAction(sessionSaveSlotNumber ?? undefined, { skipLinterCheck: true });
  };

  const dispatchStaleRunValidationFailureIfNeeded = (): boolean => {
    if (runState !== 'run' || slotStorageFresh === true) {
      return false;
    }

    const runTargetValidationError = window.robotService?.getSelectedRunTargetValidationError?.();
    if (!runTargetValidationError) {
      return false;
    }

    dispatch(
      projectSlice.actions.onUpdateProgramState({
        programState: RobotActions.PROGRAM.SEND_FAILED,
        error: runTargetValidationError,
      })
    );
    return true;
  };

  const shouldConfirmUnverifiedDeployTarget = runState !== 'run' && slotStorageFresh !== true;

  const handleRequiredSlotSelected = (selectedSlotNumber: number) => {
    if (!pendingSlotProgramAction) return;
    setPendingSlotProgramAction(false);

    if (!isConnected) {
      clearLinterBypass();
      if (userAgentInfo?.isBrowserSupported) {
        dispatch(projectSlice.actions.onOpenDrawer(ApplicationActions.Drawer.CONNECTION_DRAWER));
      }
      return;
    }

    if (programActionDisabled) {
      clearLinterBypass();
      return;
    }

    attemptProgramAction(selectedSlotNumber);
  };

  const handleRequiredSlotMenuDismissed = () => {
    setPendingSlotProgramAction(false);
    clearLinterBypass();
  };

  const handleDeployRunClick = () => {
    if (isProgramActive) {
      toggleProgramState();
      return;
    }

    if (isConnected) {
      if (programActionDisabled) return;
      if (waitingForProgramActions || isDeployingProgram) return;

      resumeConnectedDeployFlow();
    } else if (userAgentInfo?.isBrowserSupported) {
      const connectionDrawerAction = openDrawer === ApplicationActions.Drawer.CONNECTION_DRAWER ? null : ApplicationActions.Drawer.CONNECTION_DRAWER;
      dispatch(projectSlice.actions.onOpenDrawer(connectionDrawerAction));
    } else {
      dispatch(applicationSlice.actions.onOpenDialog(BLUETOOTH_WARNING_DIALOG));
    }
  };

  const handleRunMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRunMenuAnchor(event.currentTarget);
  };

  const handleRunMenuClose = () => {
    setRunMenuAnchor(null);
  };

  const handleRunModeSelect = (mode: string) => {
    setRunState(mode);
    setRunMenuAnchor(null);
  };

  const handleResetOrientation = () => {
    resetOrientation();
  };

  const deployCanPauseBackgroundFirmware = isDistributingFirmware && serviceCanPauseBackgroundFirmwareForProgramAction;
  const deployBlockedByRuntime = isConnected && isRuntimeBusy && !isProgramActive && !deployCanPauseBackgroundFirmware;
  const sharedProgramControlDisabled =
    !enableToolBar ||
    isStopToRunCooldownActive ||
    isDeployingProgram ||
    deployBlockedByRuntime ||
    (!isProgramActive && isConnected && waitingForProgramActions);
  const programActionDisabled = sharedProgramControlDisabled || (!isProgramActive && waitingForLinterDiagnostics);
  const slotSelectionDisabled =
    !enableToolBar ||
    isStopToRunCooldownActive ||
    isDeployingProgram ||
    isProgramActive ||
    slotStorageQuerying ||
    (pendingSlotProgramAction && programActionDisabled);
  const processConfirmedLinterIgnoreRef = React.useRef<() => void>(() => undefined);

  processConfirmedLinterIgnoreRef.current = () => {
    if (!pendingLinterIgnore?.confirmed) return;

    const intent = pendingLinterIgnore.intent;
    dispatch(applicationSlice.actions.clearPendingLinterIgnore());

    if (intent.projectId !== (loadedProject?.id ?? null) || intent.programCode !== programCode) {
      clearLinterBypass();
      return;
    }

    if (isProgramActive || programActionDisabled) {
      clearLinterBypass();
      return;
    }

    linterErrorsBypassedRef.current = {
      projectId: intent.projectId,
      programCode: intent.programCode,
    };

    if (intent.flow === 'resumeConnectedDeploy') {
      if (!isConnected || waitingForProgramActions || isDeployingProgram) {
        clearLinterBypass();
        return;
      }
      resumeConnectedDeployFlow({ skipLinterCheck: true });
      return;
    }

    attemptProgramAction(intent.targetSlotNumber, { skipLinterCheck: true });
  };

  useEffect(() => {
    if (!pendingLinterIgnore?.confirmed) return;
    processConfirmedLinterIgnoreRef.current();
  }, [pendingLinterIgnore]);

  useEffect(
    () => () => {
      linterErrorsBypassedRef.current = null;
      dispatch(applicationSlice.actions.clearPendingLinterIgnore());
    },
    [dispatch]
  );

  const primaryProgramIcon = isProgramActive || isDeployingProgram ? <StopIcon /> : <PlayArrowIcon />;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Reset Orientation Button — left of slot/deploy; disabled until drive unit is connected */}
        <Tooltip
          title={
            isConnected
              ? t('resetOrientationButton.tooltip', 'Reset Drive Unit Orientation')
              : t('resetOrientationButton.tooltipDisconnected', 'Connect a drive unit to reset orientation')
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={handleResetOrientation}
              disabled={!isConnected || !enableToolBar}
              aria-label={t('aria-label.reset-robot-orientation', 'reset robot orientation')}
              data-testid="reset-orientation-button"
              sx={(theme) => ({
                width: 34,
                height: 34,
                borderRadius: 1,
                bgcolor: theme.vars?.palette?.accent?.opaque || theme.palette.accent?.opaque || theme.palette.action.selected,
                color: theme.vars?.palette?.text?.primary || theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.vars?.palette?.action?.hover || theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  bgcolor: theme.vars?.palette?.action?.disabledBackground || theme.palette.action.disabledBackground,
                  color: theme.vars?.palette?.text?.disabled || theme.palette.text.disabled,
                },
              })}
            >
              <ResetOrientationIcon />
            </IconButton>
          </span>
        </Tooltip>

        {/* Slot Button */}
        <SlotButton
          disabled={slotSelectionDisabled}
          openSlotMenuSignal={openSlotMenuSignal}
          onSlotMenuDismissed={handleRequiredSlotMenuDismissed}
          onSlotSelected={handleRequiredSlotSelected}
          showForcedDeployReplaceHint={pendingSlotProgramAction}
        />

        {/* Deploy/Run Button - responsive based on screen width */}
        {isSmallScreen ? (
          // Icon-only view for small containers (accounts for drawer width)
          <>
            <Tooltip title={getRunButtonText()}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton
                  onClick={handleDeployRunClick}
                  loading={!isProgramActive && (waitingForProgramActions || isDeployingProgram || waitingForLinterDiagnostics)}
                  sx={{
                    borderRadius: '8px 0 0 8px',
                    height: 36,
                    width: 36,
                    backgroundColor: theme.vars?.palette?.accent?.opaque,
                    '&:hover': {
                      backgroundColor: theme.vars?.palette?.action?.hover,
                    },
                  }}
                  disabled={programActionDisabled}
                >
                  {primaryProgramIcon}
                </IconButton>
              </Box>
            </Tooltip>
            <IconButton
              onClick={handleRunMenuClick}
              size="small"
              sx={{
                borderRadius: '0 8px 8px 0',
                borderLeft: `1px solid ${theme.vars?.palette?.divider}`,
                width: 24,
                height: 36,
                backgroundColor: theme.vars?.palette?.accent?.opaque,
                '&:hover': {
                  backgroundColor: theme.vars?.palette?.action?.hover,
                },
              }}
              disabled={!enableToolBar}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          // Full button view for larger containers
          <>
            <Button
              onClick={handleDeployRunClick}
              loading={!isProgramActive && (waitingForProgramActions || isDeployingProgram || waitingForLinterDiagnostics)}
              loadingPosition="start"
              variant="text"
              startIcon={primaryProgramIcon}
              sx={{
                borderRadius: '8px 0 0 8px',
                height: 36,
                minWidth: 140,
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 600,
                backgroundColor: theme.vars?.palette?.accent?.opaque,
                '&:hover': {
                  backgroundColor: theme.vars?.palette?.action?.hover,
                },
              }}
              disabled={programActionDisabled}
            >
              {getRunButtonText()}
            </Button>
            <IconButton
              onClick={handleRunMenuClick}
              size="small"
              sx={{
                borderRadius: '0 8px 8px 0',
                borderLeft: `1px solid ${theme.vars?.palette?.divider}`,
                width: 24,
                height: 36,
                backgroundColor: theme.vars?.palette?.accent?.opaque,
                '&:hover': {
                  backgroundColor: theme.vars?.palette?.action?.hover,
                },
              }}
              disabled={!enableToolBar}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      <Menu anchorEl={runMenuAnchor} open={Boolean(runMenuAnchor)} onClose={handleRunMenuClose}>
        <MenuList>
          <MenuItem onClick={() => handleRunModeSelect('download')}>
            <ListItemText>
              <Trans i18nKey="menu.download-code">Download Code</Trans>
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleRunModeSelect('run')}>
            <ListItemText>
              <Trans i18nKey="menu.run-code">Run Code</Trans>
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleRunModeSelect('download_run')}>
            <ListItemText>
              <Trans i18nKey="menu.download-and-run-code">Download and Run Code</Trans>
            </ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};

export default DeployButton;
